import { Button } from "@datapay-by-datacom/design-system";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useOrgCtx } from "../../../org/hooks";
import { BindOrgRequestOrgCategory } from "../../../generated/core-api";
import {
  useGetUserGroup,
  useListUserGroupMembers,
  useListUserGroupOrgBindings,
  useAddUserGroupMember,
  useRemoveUserGroupMember,
  useBindUserGroupToOrg,
  useUnbindUserGroupFromOrg,
  useListUsersInMyOrgs,
  useUserManagementPermissions,
} from "../../hooks";
import { AddMemberModal } from "../AddMemberModal";
import { BindOrgModal } from "../BindOrgModal";
import s from "./GroupDetailView.module.css";

interface GroupDetailViewProps {
  groupId: string;
}

export function GroupDetailView({ groupId }: GroupDetailViewProps) {
  const navigate = useNavigate();
  const { canEdit } = useUserManagementPermissions();
  const { orgs } = useOrgCtx();

  const { data: group, isLoading: groupLoading, error: groupError } = useGetUserGroup(groupId);
  const { data: members, isLoading: membersLoading } = useListUserGroupMembers(groupId);
  const { data: orgBindings, isLoading: bindingsLoading } = useListUserGroupOrgBindings(groupId);
  const { data: allUsers } = useListUsersInMyOrgs();

  const addMember = useAddUserGroupMember();
  const removeMember = useRemoveUserGroupMember();
  const bindOrg = useBindUserGroupToOrg();
  const unbindOrg = useUnbindUserGroupFromOrg();

  const [showAddMember, setShowAddMember] = useState(false);
  const [showBindOrg, setShowBindOrg] = useState(false);
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set());
  const [unbindingKeys, setUnbindingKeys] = useState<Set<string>>(new Set());

  // useMemo calls must be before any early returns to satisfy Rules of Hooks
  const userMap = useMemo(
    () => new Map((allUsers ?? []).map((u) => [u.user_id, u])),
    [allUsers],
  );
  const orgMap = useMemo(
    () => new Map(orgs.map((o) => [o.id, o])),
    [orgs],
  );

  if (groupLoading) {
    return <p className={s.loadingMessage}>Loading group...</p>;
  }

  if (groupError || !group) {
    return <p className={s.errorMessage}>Failed to load group.</p>;
  }

  const memberUserIds = new Set(members?.map((m) => m.user_id) ?? []);
  const availableUsers = (allUsers ?? []).filter(
    (u) => !memberUserIds.has(u.user_id),
  );

  const boundOrgIds = new Set(orgBindings?.map((b) => b.org_id) ?? []);
  const availableOrgs = orgs.filter((org) => !boundOrgIds.has(org.id));

  const handleAddMember = (userId: string) => {
    addMember.mutate(
      { groupId, userId },
      { onSuccess: () => setShowAddMember(false) },
    );
  };

  const handleRemoveMember = (userId: string) => {
    if (removingKeys.has(userId)) return;
    setRemovingKeys((prev) => new Set(prev).add(userId));
    removeMember.mutate(
      { groupId, userId },
      {
        onSettled: () =>
          setRemovingKeys((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          }),
      },
    );
  };

  const handleBindOrg = (orgId: string, orgCategory: string) => {
    const category = orgCategory as BindOrgRequestOrgCategory;
    bindOrg.mutate(
      { groupId, request: { org_id: orgId, org_category: category } },
      { onSuccess: () => setShowBindOrg(false) },
    );
  };

  const handleUnbindOrg = (orgId: string) => {
    if (unbindingKeys.has(orgId)) return;
    setUnbindingKeys((prev) => new Set(prev).add(orgId));
    unbindOrg.mutate(
      { groupId, orgId },
      {
        onSettled: () =>
          setUnbindingKeys((prev) => {
            const next = new Set(prev);
            next.delete(orgId);
            return next;
          }),
      },
    );
  };

  const findUserName = (userId: string) => {
    const user = userMap.get(userId);
    return user?.name || user?.email || userId;
  };

  const findOrgName = (orgId: string) => orgMap.get(orgId)?.name || orgId;

  return (
    <div className={s.container}>
      <button
        type="button"
        className={s.backLink}
        onClick={() => navigate("/user-management/groups")}
      >
        &larr; Back to Groups
      </button>

      <div className={s.header}>
        <div className={s.headerTop}>
          <h2 className={s.groupName}>{group.name}</h2>
          <Link to={`/user-management/groups/${groupId}/analytics`} className={s.analyticsLink}>
            Analytics &rarr;
          </Link>
        </div>
        {group.description && (
          <p className={s.groupDescription}>{group.description}</p>
        )}
        <div className={s.groupMeta}>
          <span>Created by {group.created_by}</span>
          <span>
            {new Date(group.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Members Section */}
      <div className={s.section}>
        <div className={s.sectionHeader}>
          <h3 className={s.sectionTitle}>Members</h3>
          {canEdit && (
            <Button
              variant="secondary"
              onPress={() => setShowAddMember(true)}
            >
              Add Member
            </Button>
          )}
        </div>

        {membersLoading ? (
          <p className={s.loadingMessage}>Loading members...</p>
        ) : !members || members.length === 0 ? (
          <p className={s.emptyMessage}>No members in this group</p>
        ) : (
          <ul className={s.itemList}>
            {members.map((member) => {
              const isRemoving = removingKeys.has(member.user_id);
              return (
                <li key={member.user_id} className={s.item}>
                  <div className={s.itemInfo}>
                    <span className={s.itemName}>
                      {findUserName(member.user_id)}
                    </span>
                    <span className={s.itemDetail}>
                      Added {new Date(member.added_at).toLocaleDateString()}
                    </span>
                  </div>
                  {canEdit && (
                    <button
                      className={s.removeButton}
                      onClick={() => handleRemoveMember(member.user_id)}
                      disabled={isRemoving}
                      aria-label={`Remove ${findUserName(member.user_id)}`}
                    >
                      {isRemoving ? "Removing..." : "Remove"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Org Bindings Section */}
      <div className={s.section}>
        <div className={s.sectionHeader}>
          <h3 className={s.sectionTitle}>Organization Bindings</h3>
          {canEdit && (
            <Button
              variant="secondary"
              onPress={() => setShowBindOrg(true)}
            >
              Bind Organization
            </Button>
          )}
        </div>

        {bindingsLoading ? (
          <p className={s.loadingMessage}>Loading bindings...</p>
        ) : !orgBindings || orgBindings.length === 0 ? (
          <p className={s.emptyMessage}>
            No organizations bound to this group
          </p>
        ) : (
          <ul className={s.itemList}>
            {orgBindings.map((binding) => {
              const isUnbinding = unbindingKeys.has(binding.org_id);
              return (
                <li key={binding.org_id} className={s.item}>
                  <div className={s.itemInfo}>
                    <span className={s.itemName}>
                      {findOrgName(binding.org_id)}
                    </span>
                    <span className={s.categoryBadge}>
                      {binding.org_category}
                    </span>
                    <span className={s.itemDetail}>
                      Bound {new Date(binding.bound_at).toLocaleDateString()}
                    </span>
                  </div>
                  {canEdit && (
                    <button
                      className={s.removeButton}
                      onClick={() => handleUnbindOrg(binding.org_id)}
                      disabled={isUnbinding}
                      aria-label={`Unbind ${findOrgName(binding.org_id)}`}
                    >
                      {isUnbinding ? "Unbinding..." : "Unbind"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AddMemberModal
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
        isPending={addMember.isPending}
        availableUsers={availableUsers}
      />

      <BindOrgModal
        isOpen={showBindOrg}
        onClose={() => setShowBindOrg(false)}
        onBind={handleBindOrg}
        isPending={bindOrg.isPending}
        availableOrgs={availableOrgs}
      />
    </div>
  );
}
